import React, { useState, useEffect } from 'react';
import axiosInstance from '../../../api/axiosInstance';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Alert,
    AlertDescription,
    AlertIcon,
    Badge,
    Box,
    Collapse,
    Switch,
    FormControl,
    FormLabel,
    Input,
    Button,
    Text,
    Flex,
    Select,
    SimpleGrid,
    IconButton,
    Spinner,
    Tooltip,
    Image,
    Heading,
    useDisclosure,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon, DeleteIcon } from '@chakra-ui/icons';
import { useNotification } from '../../../contexts/NotificationContext';
import ThemePresetPicker from '../../../components/fields/ThemePresetPicker';

const normalizeThemePresets = (presets) => {
    if (!Array.isArray(presets)) {
        return [];
    }

    return presets
        .filter((preset) => typeof preset?.value === 'string' && preset.value.trim())
        .map((preset) => ({
            value: preset.value.trim(),
            label: typeof preset.label === 'string' && preset.label.trim() ? preset.label.trim() : preset.value.trim(),
            description: typeof preset.description === 'string' ? preset.description : '',
            cta_color: preset.cta_color,
            highlight_color: preset.highlight_color,
            accent_color: preset.accent_color,
            button_text_color: preset.button_text_color,
        }));
};

const getInitialThemePreset = (data, presets) => {
    const presetValues = presets.map((preset) => preset.value);
    const selectedThemePreset = typeof data?.selected_theme_preset === 'string'
        ? data.selected_theme_preset.trim()
        : '';
    const savedThemePreset = typeof data?.theme_preset === 'string'
        ? data.theme_preset.trim()
        : '';

    if (selectedThemePreset && (!presetValues.length || presetValues.includes(selectedThemePreset))) {
        return selectedThemePreset;
    }

    if (savedThemePreset && (!presetValues.length || presetValues.includes(savedThemePreset))) {
        return savedThemePreset;
    }

    if (presetValues.includes('default')) {
        return 'default';
    }

    return presets[0]?.value || '';
};

const EditorSection = ({ eyebrow, title, description, action, children }) => (
    <Box mb="6" p={{ base: 4, md: 6 }} borderWidth="1px" borderColor="gray.200" borderRadius="2xl" bg="white" boxShadow="sm">
        <Flex justify="space-between" align={{ base: 'flex-start', md: 'center' }} gap="4" mb="5" direction={{ base: 'column', md: 'row' }}>
            <Box>
                {eyebrow && (
                    <Badge colorScheme="blue" borderRadius="full" mb="2" px="3" py="1">
                        {eyebrow}
                    </Badge>
                )}
                <Heading size="md" color="gray.800">
                    {title}
                </Heading>
                {description && (
                    <Text color="gray.500" fontSize="sm" mt="1">
                        {description}
                    </Text>
                )}
            </Box>
            {action}
        </Flex>
        {children}
    </Box>
);

const FormEditor = () => {
    const { formId, productId } = useParams();
    const navigate = useNavigate();
    const [form, setForm] = useState({
        fields: [],
        description: '',
        shipping_label: '',
        shipping_field: '',
        shipping_value: '',
        upsells: [],
        terms_text: '',
        terms_link_text: '',
        terms_link_url: '',
        terms_checked_by_default: false,
        theme_preset: '',
    });
    const [products, setProducts] = useState([]);
    const [themePresets, setThemePresets] = useState([]);
    const [themePresetStatus, setThemePresetStatus] = useState({
        isLoading: !!formId,
        error: '',
    });
    const [isFormLoading, setIsFormLoading] = useState(!!formId);
    const [formLoadError, setFormLoadError] = useState('');
    const { addNotification, addErrorNotifications } = useNotification();
    const {
        isOpen: isMobileThemePickerOpen,
        onToggle: toggleMobileThemePicker,
    } = useDisclosure();
    const fieldOptions = ['name', 'phone', 'county', 'city', 'address'];

    useEffect(() => {
        let isMounted = true;

        const applyThemePresetResponse = (data) => {
            const normalizedThemePresets = normalizeThemePresets(data.available_theme_presets);

            setThemePresets(normalizedThemePresets);
            setThemePresetStatus({
                isLoading: false,
                error: Array.isArray(data.available_theme_presets)
                    ? ''
                    : 'Theme presets are currently unavailable for this form.',
            });

            return normalizedThemePresets;
        };

        if (formId) {
            const fetchForm = async () => {
                setIsFormLoading(true);
                setFormLoadError('');
                setThemePresetStatus({
                    isLoading: true,
                    error: '',
                });

                try {
                    const response = await axiosInstance.get(`/forms/${formId}`);
                    const data = response.data || {};
                    const upsells = Array.isArray(data.upsells)
                        ? data.upsells.map((u) => ({
                            ...u,
                            quantity: (u.quantity ?? 1),
                        }))
                        : [];
                    const rest = { ...data };
                    delete rest.available_theme_presets;
                    delete rest.selected_theme_preset;

                    if (!isMounted) {
                        return;
                    }

                    const normalizedThemePresets = applyThemePresetResponse(data);
                    setForm((currentForm) => ({
                        ...currentForm,
                        ...rest,
                        upsells,
                        terms_checked_by_default: !!data.terms_checked_by_default,
                        theme_preset: getInitialThemePreset(data, normalizedThemePresets),
                    }));
                } catch (error) {
                    if (!isMounted) {
                        return;
                    }

                    setThemePresets([]);
                    setFormLoadError('We could not load this form right now.');
                    setThemePresetStatus({
                        isLoading: false,
                        error: 'Theme presets could not be loaded right now.',
                    });
                    addErrorNotifications(error.response?.data || error);
                } finally {
                    if (isMounted) {
                        setIsFormLoading(false);
                    }
                }
            };
            fetchForm();
        } else {
            setIsFormLoading(false);
            setFormLoadError('');
            setThemePresetStatus({
                isLoading: true,
                error: '',
            });

            const fetchThemePresets = async () => {
                try {
                    const response = await axiosInstance.get('/forms/theme-presets');
                    const data = response.data || {};

                    if (!isMounted) {
                        return;
                    }

                    const normalizedThemePresets = applyThemePresetResponse(data);
                    setForm((currentForm) => ({
                        ...currentForm,
                        theme_preset: getInitialThemePreset(data, normalizedThemePresets),
                    }));
                } catch (error) {
                    if (!isMounted) {
                        return;
                    }

                    setThemePresets([]);
                    setThemePresetStatus({
                        isLoading: false,
                        error: 'Theme presets could not be loaded right now.',
                    });
                    addErrorNotifications(error.response?.data || error);
                }
            };

            fetchThemePresets();
        }

        const fetchProducts = async () => {
            try {
                const response = await axiosInstance.get(`/products/dictionary`);

                if (isMounted) {
                    setProducts(response.data);
                }
            } catch (error) {
                if (isMounted) {
                    addErrorNotifications(error.response?.data || error);
                }
            }
        };
        fetchProducts();

        return () => {
            isMounted = false;
        };
    }, [formId, addErrorNotifications]);

    const handleFieldChange = (index, field, value) => {
        const newFields = [...form.fields];
        newFields[index][field] = value;
        setForm({ ...form, fields: newFields });
    };

    const handleUpsellChange = (index, field, value) => {
        const newUpsells = [...form.upsells];
        newUpsells[index][field] = value;
        setForm({ ...form, upsells: newUpsells });
    };

    const handleImageChange = (index, event) => {
        const file = event.target.files[0];

        const newUpsells = [...form.upsells];
        if (file) {
            newUpsells[index].image = file;
            newUpsells[index].image_url = URL.createObjectURL(file);
        } else {
            newUpsells[index].image = null;
            newUpsells[index].image_url = '';
        }

        setForm({ ...form, upsells: newUpsells });
    };

    const addField = () => {
        setForm({
            ...form,
            fields: [...form.fields, { name: '', label: '', description: '', type: 'text' }],
        });
    };

    const removeField = (index) => {
        const newFields = form.fields.filter((_, i) => i !== index);
        setForm({ ...form, fields: newFields });
    };

    const addUpsell = () => {
        setForm({
            ...form,
            upsells: [
                ...form.upsells,
                { product_id: '', value: '', image: null, image_url: '', full_text: '', quantity: 1 },
            ],
        });
    };

    const removeUpsell = (index) => {
        const newUpsells = form.upsells.filter((_, i) => i !== index);
        setForm({ ...form, upsells: newUpsells });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const formData = new FormData();
        formData.append('description', form.description);
        formData.append('shipping_label', form.shipping_label);
        formData.append('shipping_field', form.shipping_field);
        formData.append('shipping_value', form.shipping_value);
        formData.append('product_id', productId);
        formData.append('theme_preset', form.theme_preset || '');

        formData.append('terms_text', form.terms_text || '');
        formData.append('terms_link_text', form.terms_link_text || '');
        formData.append('terms_link_url', form.terms_link_url || '');
        formData.append('terms_checked_by_default', form.terms_checked_by_default ? '1' : '0');

        form.fields.forEach((field, index) => {
            formData.append(`fields[${index}][name]`, field.name);
            formData.append(`fields[${index}][label]`, field.label);
            formData.append(`fields[${index}][description]`, field.description);
            formData.append(`fields[${index}][type]`, field.type);
        });

        form.upsells.forEach((upsell, index) => {
            formData.append(`upsells[${index}][product_id]`, upsell.product_id);
            formData.append(`upsells[${index}][value]`, upsell.value);
            formData.append(`upsells[${index}][full_text]`, upsell.full_text);
            formData.append(`upsells[${index}][quantity]`, upsell.quantity ?? 1);

            if (upsell.image) {
                formData.append(`upsells[${index}][image]`, upsell.image);
            }

            if (upsell.image_url) {
                formData.append(`upsells[${index}][image_url]`, upsell.image_url);
            }
        });

        try {
            if (formId) {
                formData.append(`_method`, 'PUT');
                await axiosInstance.post(`/forms/${formId}`, formData);
                addNotification('Form updated successfully', 'success');
            } else {
                await axiosInstance.post('/forms', formData);
                addNotification('Form created successfully', 'success');
            }
            navigate('/admin/products');
        } catch (error) {
            addErrorNotifications(error.response?.data || error);
        }
    };

    const getAvailableOptions = (currentName) => {
        const selectedNames = form.fields.map((field) => field.name);
        return fieldOptions.filter((option) => !selectedNames.includes(option) || option === currentName);
    };

    const selectedThemePreset = themePresets.find((preset) => preset.value === form.theme_preset);
    const formMode = formId ? 'Edit form' : 'Create form';
    const themePickerEmptyMessage = formId
        ? 'No theme presets were returned for this form.'
        : 'No theme presets are available right now.';

    const renderThemePicker = (columns) => (
        <ThemePresetPicker
            presets={themePresets}
            value={form.theme_preset}
            onChange={(nextThemePreset) => setForm({
                ...form,
                theme_preset: nextThemePreset,
            })}
            columns={columns}
            isLoading={themePresetStatus.isLoading}
            errorMessage={themePresetStatus.error}
            emptyMessage={themePickerEmptyMessage}
        />
    );

    return (
        <Box
            maxWidth="xxl"
            px={{ base: '20px', md: '40px' }}
            py={{ base: '120px', md: '80px' }}
            bg="gray.50"
            minH="100vh"
        >
            <form onSubmit={handleSubmit}>
                <Flex mb="6" direction={{ base: 'column', md: 'row' }} justify="space-between" gap="4">
                    <Box>
                        <Text color="gray.500" mt="2">
                            Configure the customer form, shipping, terms, upsells and checkout theme.
                        </Text>
                    </Box>
                    <Flex align="center">
                        <Badge colorScheme="purple" borderRadius="full" px="3" py="1">
                            {selectedThemePreset?.label || 'No theme selected'}
                        </Badge>
                    </Flex>
                </Flex>

                {formLoadError && (
                    <Alert status="error" borderRadius="lg" mb="6">
                        <AlertIcon />
                        <AlertDescription>{formLoadError}</AlertDescription>
                    </Alert>
                )}

                {isFormLoading && (
                    <Box
                        mb="6"
                        px="6"
                        py="8"
                        borderWidth="1px"
                        borderStyle="dashed"
                        borderColor="gray.200"
                        borderRadius="lg"
                        bg="gray.50"
                    >
                        <Flex direction="column" align="center" justify="center" gap="3">
                            <Spinner color="blue.500" thickness="3px" />
                            <Text fontSize="sm" color="gray.500">
                                Loading form settings...
                            </Text>
                        </Flex>
                    </Box>
                )}

                <Flex direction={{ base: 'column', xl: 'row' }} align="flex-start" gap={{ base: '8', xl: '10' }}>
                    <Box flex="1" minW="0" w="100%">
                        <EditorSection
                            eyebrow="Checkout"
                            title="Core form settings"
                            description="Set the checkout copy and shipping values customers see before submitting."
                        >
                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing="4">
                                <FormControl gridColumn={{ md: 'span 2' }}>
                                    <FormLabel>Description</FormLabel>
                                    <Input
                                        value={form.description}
                                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                                        placeholder="Short checkout description"
                                    />
                                </FormControl>
                                <FormControl>
                                    <FormLabel>Shipping Label</FormLabel>
                                    <Input
                                        value={form.shipping_label}
                                        onChange={(e) => setForm({ ...form, shipping_label: e.target.value })}
                                        placeholder="e.g. Delivery"
                                    />
                                </FormControl>
                                <FormControl>
                                    <FormLabel>Shipping Field</FormLabel>
                                    <Input
                                        value={form.shipping_field}
                                        onChange={(e) => setForm({ ...form, shipping_field: e.target.value })}
                                        placeholder="e.g. courier"
                                    />
                                </FormControl>
                                <FormControl>
                                    <FormLabel>Shipping Value</FormLabel>
                                    <Input
                                        type="number"
                                        value={form.shipping_value}
                                        onChange={(e) => setForm({ ...form, shipping_value: e.target.value })}
                                        placeholder="Shipping price"
                                    />
                                </FormControl>
                            </SimpleGrid>
                        </EditorSection>

                        <Box display={{ base: 'block', xl: 'none' }} mb="6" p="5" bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="2xl" boxShadow="sm">
                            <Flex justify="space-between" align={{ base: 'flex-start', md: 'center' }} gap="4" mb="3" direction={{ base: 'column', md: 'row' }}>
                                <Box>
                                    <Text fontSize="lg" fontWeight="bold">
                                        Theme Preset
                                    </Text>
                                    <Text fontSize="sm" color="gray.500">
                                        {selectedThemePreset?.label || 'Choose a checkout look'}
                                    </Text>
                                </Box>
                                <Button
                                    type="button"
                                    onClick={toggleMobileThemePicker}
                                    variant="outline"
                                    rightIcon={isMobileThemePickerOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                                >
                                    {isMobileThemePickerOpen ? 'Hide themes' : 'Change theme'}
                                </Button>
                            </Flex>
                            <Collapse in={isMobileThemePickerOpen} animateOpacity>
                                <Text fontSize="sm" color="gray.500" mb="4">
                                    Choose the palette customers see on the checkout form. Your selection is saved as the
                                    backend theme preset slug.
                                </Text>
                                {renderThemePicker({ base: 1, md: 2 })}
                            </Collapse>
                        </Box>

                        <EditorSection
                            eyebrow="Legal"
                            title="Terms and conditions"
                            description="Optional customer consent copy shown next to the checkout checkbox."
                        >
                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing="4">
                                <FormControl>
                                    <FormLabel>General Text (e.g., "I agree to the")</FormLabel>
                                    <Input
                                        placeholder="Am citit si sunt de acord cu"
                                        value={form.terms_text}
                                        onChange={(e) => setForm({ ...form, terms_text: e.target.value })}
                                    />
                                </FormControl>

                                <FormControl>
                                    <FormLabel>Link Text (The highlighted part)</FormLabel>
                                    <Input
                                        placeholder="Termenii si Conditiile"
                                        value={form.terms_link_text}
                                        onChange={(e) => setForm({ ...form, terms_link_text: e.target.value })}
                                    />
                                </FormControl>

                                <FormControl>
                                    <FormLabel>Link URL</FormLabel>
                                    <Input
                                        placeholder="https://site.com/termeni"
                                        value={form.terms_link_url}
                                        onChange={(e) => setForm({ ...form, terms_link_url: e.target.value })}
                                    />
                                </FormControl>

                                <FormControl display="flex" alignItems="center">
                                    <FormLabel mb="0">Checked by default?</FormLabel>
                                    <Switch
                                        isChecked={form.terms_checked_by_default}
                                        onChange={(e) => setForm({ ...form, terms_checked_by_default: e.target.checked })}
                                    />
                                </FormControl>
                            </SimpleGrid>

                            <Box mt="4" p="4" borderWidth="1px" borderStyle="dashed" borderColor="gray.300" borderRadius="xl" bg="gray.50">
                                <Text fontSize="xs" color="gray.500">Preview:</Text>
                                <Flex align="center" gap="2">
                                    <input type="checkbox" checked={form.terms_checked_by_default} readOnly />
                                    <Text fontSize="sm">
                                        {form.terms_text}{' '}
                                        <span style={{ color: 'blue', textDecoration: 'underline', cursor: 'pointer' }}>
                                            {form.terms_link_text}
                                        </span>
                                    </Text>
                                </Flex>
                            </Box>
                        </EditorSection>

                        <EditorSection
                            eyebrow="Inputs"
                            title="Customer fields"
                            description="Choose which customer details appear on the checkout form and how they are labelled."
                            action={(
                                <Button colorScheme="blue" onClick={addField} width={{ base: 'full', md: 'auto' }}>
                                    Add Field
                                </Button>
                            )}
                        >
                            {form.fields.map((field, index) => (
                                <SimpleGrid
                                    columns={{ base: 1, md: 5 }}
                                    spacing="4"
                                    mb="4"
                                    key={index}
                                    p="4"
                                    borderWidth="1px"
                                    borderColor="gray.200"
                                    borderRadius="xl"
                                    bg="gray.50"
                                    alignItems="end"
                                >
                                    <FormControl>
                                        <FormLabel>Name</FormLabel>
                                        <Select
                                            value={field.name}
                                            onChange={(e) => handleFieldChange(index, 'name', e.target.value)}
                                            placeholder="Select a field"
                                        >
                                            {getAvailableOptions(field.name).map((option) => (
                                                <option key={option} value={option}>
                                                    {option}
                                                </option>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <FormControl>
                                        <FormLabel>Label</FormLabel>
                                        <Input
                                            value={field.label}
                                            onChange={(e) => handleFieldChange(index, 'label', e.target.value)}
                                        />
                                    </FormControl>
                                    <FormControl>
                                        <FormLabel>Description</FormLabel>
                                        <Input
                                            value={field.description}
                                            onChange={(e) => handleFieldChange(index, 'description', e.target.value)}
                                        />
                                    </FormControl>
                                    <FormControl>
                                        <FormLabel>Type</FormLabel>
                                        <Select
                                            value={field.type}
                                            onChange={(e) => handleFieldChange(index, 'type', e.target.value)}
                                        >
                                            <option value="text">Text</option>
                                            <option value="tel">Number</option>
                                        </Select>
                                    </FormControl>
                                    <Flex alignItems="center" justifyContent="center">
                                        <Tooltip label="Delete Field">
                                            <IconButton
                                                icon={<DeleteIcon />}
                                                aria-label="delete"
                                                onClick={() => removeField(index)}
                                                colorScheme="red"
                                            />
                                        </Tooltip>
                                    </Flex>
                                </SimpleGrid>
                            ))}
                        </EditorSection>

                        <EditorSection
                            eyebrow="Revenue"
                            title="Upsell products"
                            description="Offer extra products after the main selection without changing the checkout flow."
                            action={(
                                <Button colorScheme="blue" onClick={addUpsell} width={{ base: 'full', md: 'auto' }}>
                                    Add Upsell
                                </Button>
                            )}
                        >
                            {form.upsells.map((upsell, index) => (
                                <SimpleGrid
                                    columns={{ base: 1, md: 6 }}
                                    spacing="4"
                                    mb="4"
                                    key={index}
                                    p="4"
                                    borderWidth="1px"
                                    borderColor="gray.200"
                                    borderRadius="xl"
                                    bg="gray.50"
                                    alignItems="start"
                                >
                                    <FormControl>
                                        <FormLabel>Product</FormLabel>
                                        <Select
                                            value={upsell.product_id}
                                            onChange={(e) => handleUpsellChange(index, 'product_id', e.target.value)}
                                            placeholder="Select product"
                                        >
                                            {products.map((product) => (
                                                <option key={product.id} value={product.id}>{product.name}</option>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <FormControl>
                                        <FormLabel>Price</FormLabel>
                                        <Input
                                            type="number"
                                            value={upsell.value}
                                            onChange={(e) => handleUpsellChange(index, 'value', e.target.value)}
                                        />
                                    </FormControl>

                                    <FormControl>
                                        <FormLabel>Quantity</FormLabel>
                                        <Input
                                            type="number"
                                            min={1}
                                            step={1}
                                            value={upsell.quantity ?? 1}
                                            onChange={(e) => {
                                                const v = e.target.value;
                                                const next = Math.max(1, Number.isNaN(Number(v)) ? 1 : parseInt(v, 10));
                                                handleUpsellChange(index, 'quantity', next);
                                            }}
                                        />
                                    </FormControl>

                                    <FormControl>
                                        <FormLabel>Full Text</FormLabel>
                                        <Input
                                            value={upsell.full_text}
                                            onChange={(e) => handleUpsellChange(index, 'full_text', e.target.value)}
                                        />
                                    </FormControl>

                                    <FormControl>
                                        <FormLabel>Image</FormLabel>
                                        <Input
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => handleImageChange(index, e)}
                                        />
                                        {upsell.image_url && (
                                            <Box position="relative" mt="4">
                                                <Image src={upsell.image_url} alt="option" boxSize="100px" objectFit="cover" rounded="md" />
                                                <IconButton
                                                    icon={<DeleteIcon />}
                                                    aria-label="delete image"
                                                    colorScheme="red"
                                                    position="absolute"
                                                    top="0"
                                                    right="0"
                                                    onClick={() => handleImageChange(index, { target: { files: [null] } })}
                                                />
                                            </Box>
                                        )}
                                    </FormControl>

                                    <Flex alignItems="center" justifyContent="center">
                                        <Tooltip label="Delete Upsell">
                                            <IconButton
                                                icon={<DeleteIcon />}
                                                aria-label="delete upsell"
                                                onClick={() => removeUpsell(index)}
                                                colorScheme="red"
                                            />
                                        </Tooltip>
                                    </Flex>
                                </SimpleGrid>
                            ))}
                        </EditorSection>

                        <Flex
                            justify="flex-end"
                            p="5"
                            bg="white"
                            borderWidth="1px"
                            borderColor="gray.200"
                            borderRadius="2xl"
                            boxShadow="sm"
                        >
                            <Button type="submit" colorScheme="teal" size="lg" width={{ base: 'full', md: 'auto' }}>
                                Save form
                            </Button>
                        </Flex>
                    </Box>

                    <Box
                        display={{ base: 'none', xl: 'block' }}
                        w={{ xl: '360px', '2xl': '400px' }}
                        flexShrink="0"
                        position="sticky"
                        top="24px"
                        maxH="calc(100vh - 48px)"
                    >
                        <Box
                            p="5"
                            bg="white"
                            borderRadius="2xl"
                            borderWidth="1px"
                            borderColor="gray.200"
                            boxShadow="sm"
                            h="100%"
                            maxH="calc(100vh - 48px)"
                            display="flex"
                            flexDirection="column"
                            overflow="hidden"
                        >
                            <Text fontSize="xl" fontWeight="bold" mb="2" flexShrink="0">
                                Theme Preset
                            </Text>
                            <Text fontSize="sm" color="gray.500" mb="2" flexShrink="0">
                                Keep this visible while editing and compare the checkout feel without losing your place.
                            </Text>
                            <Text fontSize="sm" fontWeight="medium" color="gray.700" mb="4" flexShrink="0">
                                Selected: {selectedThemePreset?.label || 'No theme selected'}
                            </Text>
                            <Box
                                flex="1"
                                minH="0"
                                overflowY="auto"
                                pr="1"
                                sx={{
                                    scrollbarGutter: 'stable',
                                }}
                            >
                                {renderThemePicker(1)}
                            </Box>
                        </Box>
                    </Box>
                </Flex>
            </form>
        </Box>
    );
};

export default FormEditor;